<template>
    <div class="q-pa-md">
        <div class="column q-col-gutter-md">
            <div class="row q-col-gutter-x-lg">
                <!-- company name -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="company.title"
                        :label="$t('general.name')"
                        type="text"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :error-message="companyTitleErrors"
                        :error="companyTitleErrors != ''"
                        @input="$v.company.title.$touch()"
                        @blur="$v.company.title.$touch()"
                    />
                </div>
                <!-- email -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model.trim="company.email"
                        :label="$t('general.email')"
                        type="email"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="250"
                        :error-message="companyEmailErrors"
                        :error="companyEmailErrors != ''"
                        @input="$v.company.email.$touch()"
                        @blur="$v.company.email.$touch()"
                    />
                </div>
                <!-- phone number -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-input
                        v-model="company.phone"
                        :label="$t('general.phone')"
                        type="text"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="20"
                        :error-message="companyPhoneErrors"
                        :error="companyPhoneErrors != ''"
                        @input="$v.company.phone.$touch()"
                        @blur="$v.company.phone.$touch()"
                    />
                </div>
                <!-- tax number -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-input
                        v-model="company.tax_number"
                        :label="$t('general.tax_number')"
                        type="text"
                        bg-color="white"
                        outlined
                        clearable
                        counter
                        maxlength="15"
                        :error-message="companyTaxNumberErrors"
                        :error="companyTaxNumberErrors != ''"
                        @input="$v.company.tax_number.$touch()"
                        @blur="$v.company.tax_number.$touch()"
                    />
                </div>
                <!-- currency -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-select
                        v-model="company.currency_id"
                        class="q-mb-md text-uppercase"
                        :label="$t('general.currency')"
                        bg-color="white"
                        :options="currencyOptions"
                        option-value="id"
                        option-label="key"
                        clearable
                        outlined
                        map-options
                        emit-value
                        :error-message="companyCurrencyErrors"
                        :error="companyCurrencyErrors != ''"
                        @input="$v.company.currency_id.$touch()"
                        @blur="$v.company.currency_id.$touch()"
                    >
                        <template v-slot:option="scope">
                            <q-item
                                v-bind="scope.itemProps"
                                v-on="scope.itemEvents"
                            >
                                <q-item-section>
                                    <q-item-label class="text-capitalize"
                                        >{{ scope.opt.name }}
                                        <span class="text-uppercase"
                                            >({{ scope.opt.key }})</span
                                        >
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                        </template>
                    </q-select>
                </div>
                <!-- Country select -->
                <div class="col-12 col-md-3 q-pb-lg">
                    <q-select
                        v-model="company.country_id"
                        class="q-mb-md"
                        :label="$t('general.country')"
                        bg-color="white"
                        hide-selected
                        outlined
                        input-debounce="0"
                        option-value="id"
                        option-label="name"
                        map-options
                        :options="filteredCountries"
                        emit-value
                        use-input
                        fill-input
                        clearable
                        :disable="hasErpIntegration"
                        :error-message="companyCountryErrors"
                        :error="companyCountryErrors != ''"
                        @filter="filterCountries"
                        @input="$v.company.country_id.$touch()"
                        @blur="$v.company.country_id.$touch()"
                    />
                </div>
                <!-- street -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="company.street"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :label="$t('general.street')"
                        bg-color="white"
                        :error-message="companyStreetErrors"
                        :error="companyStreetErrors != ''"
                        @input="$v.company.street.$touch()"
                        @blur="$v.company.street.$touch()"
                    />
                </div>
                <!-- city -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="company.city"
                        outlined
                        clearable
                        counter
                        maxlength="100"
                        :label="$t('general.city')"
                        bg-color="white"
                        :error-message="companyCityErrors"
                        :error="companyCityErrors != ''"
                        @input="$v.company.city.$touch()"
                        @blur="$v.company.city.$touch()"
                    />
                </div>
                <!-- state -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="company.state"
                        outlined
                        clearable
                        counter
                        maxlength="35"
                        :label="$t('general.state')"
                        bg-color="white"
                        :error-message="companyStateErrors"
                        :error="companyStateErrors != ''"
                        @input="$v.company.state.$touch()"
                        @blur="$v.company.state.$touch()"
                    />
                </div>
                <!-- zip code -->
                <div class="col-12 col-md-6 q-pb-lg">
                    <q-input
                        v-model="company.zip_code"
                        outlined
                        clearable
                        counter
                        maxlength="20"
                        :label="$t('general.zip_code')"
                        bg-color="white"
                    />
                </div>
            </div>
            <!-- save button -->
            <div class="col self-end">
                <q-btn
                    color="secondary"
                    :label="$t('actions.save')"
                    :disable="isSaving"
                    @click="saveClientData"
                />
            </div>
        </div>
    </div>
</template>

<script>
import validations from '@/views/profile/my-organization/mixins/validations'
import clientsService from '@/services/modules/clients'
import currenciesService from '@/services/modules/currencies'
import countriesService from '@/services/modules/profile/countries'
import store from '@/store'
import { mapGetters } from 'vuex'

const loadingKeys = {
    savingData: 'saving-data',
    fetchingClient: 'fetching-client',
    fetchingCountries: 'fetching-countries',
    fetchingCurrencies: 'fetching-currencies',
}

export default {
    name: 'MyOrganizationView',

    components: {},

    mixins: [validations],

    data() {
        return {
            company: {
                title: '',
                email: '',
                phone: '',
                tax_number: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                country_id: null,
            },

            countryOptions: [],
            filteredCountries: [],

            currencyOptions: [],
        }
    },

    computed: {
        ...mapGetters('integrations', ['hasErpIntegration']),

        isSaving() {
            return this.isSavingData
        },

        isSavingData() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.savingData
            ).length
        },

        isLoading() {
            return this.isFetchingClient
        },

        isFetchingClient() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingClient
            ).length
        },

        storedUser() {
            return store.getters['session/user']
        },

        firstAffiliation() {
            if (
                !this.storedUser ||
                !this.storedUser.affiliations ||
                !this.storedUser.affiliations.length
            ) {
                return null
            }
            return this.storedUser.affiliations[0]
        },
    },

    async created() {
        await Promise.all([this.fetchCountries(), this.fetchCurrencies()])
        this.fetchClient()
    },

    methods: {
        async saveClientData() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                return true
            }

            this.loadingAdd(loadingKeys.savingData)

            let parameters = {
                data: [this.company],
            }

            let response = await clientsService.update(parameters)

            if (response.status === 200) {
                this.$q.notify({
                    type: 'positive',
                    message: this.$t('notify.client_profile_updated'),
                })
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.savingData)
        },

        async fetchClient() {
            if (!this.firstAffiliation) {
                return
            }

            this.loadingAdd(loadingKeys.fetchingUser)

            let parameters = {
                filter: {
                    id: this.firstAffiliation.clients.id,
                },
            }

            let response = await clientsService.retrieve(parameters)

            if (response.status === 200) {
                this.company = response.data.results[0]
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.error_occurred', {
                        message: response.data.message,
                    }),
                })
            }

            this.loadingRemove(loadingKeys.fetchingUser)
        },

        filterCountries(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredCountries = this.countryOptions.filter(
                    (v) => v.name.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        async fetchCountries() {
            this.loadingAdd(loadingKeys.fetchingCountries)

            let parameters = {
                filter: {},
            }

            let response = await countriesService.retrieve(parameters)

            if (response.status === 200) {
                this.countryOptions = response.data.results
                this.filteredCountries = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCountries)
        },

        async fetchCurrencies() {
            this.loadingAdd(loadingKeys.fetchingCurrencies)

            let parameters = {
                filter: {},
            }

            let response = await currenciesService.retrieve(parameters)

            if (response.status === 200) {
                this.currencyOptions = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCurrencies)
        },
    },
}
</script>
